import { computed, inject, Injectable, Signal } from '@angular/core';
import { AppForm } from '../../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import {
  ObrebAppEntityDictionaryObreb,
  ObrebService,
  TreeRemovalReasonAppEntityDictionaryTreeRemovalReason,
  TreeToRemoveAppEntityTreeToRemoveWrite
} from '../../../../../../../openapi';
import { Store } from '@ngrx/store';
import { selectTreeRemovalReasons } from '../../../../../../../store/selectors/application.selectors';
import { toSignal } from "@angular/core/rxjs-interop";
import {
  ObjectTypeEnum,
  ShrubSpeciesItem,
  TreeSpeciesItem,
  TreeToRemoveCommonService
} from "../../../../../../../services/tree-to-remove.common.service";
import { TrunkCircumference130Form } from "./trunkCircumference130.form";

export class RemovalReasonForm extends AppForm {

  private store$: Store = inject(Store);

  public removalReasons: Signal<TreeRemovalReasonAppEntityDictionaryTreeRemovalReason[]> = computed(() => {
    const reasons = this.store$.selectSignal(selectTreeRemovalReasons)();
    this.initForm(reasons);
    return reasons;
  });

  constructor() {
    super({});
  }

  public get unchecked(): boolean {
    return Object.keys(this.controls).map(v => this.get(v)?.value).filter(v => v).length === 0;
  }

  public initForm(v: TreeRemovalReasonAppEntityDictionaryTreeRemovalReason[]): void {
    this.clearForm();
    v.forEach((removalReason) => {
      this.addControl(removalReason?.code || '', new FormControl(false));
    });
  }

  private clearForm(): void {
    const controls = Object.keys(this.controls);
    controls.forEach(c => this.removeControl(c));
  }

  public compareValues(): TreeRemovalReasonAppEntityDictionaryTreeRemovalReason[] {
    const result: TreeRemovalReasonAppEntityDictionaryTreeRemovalReason[] = [];
    Object.keys(this.controls).forEach(v => {
      if (this.get(v)?.value) {
        result.push(this.removalReasons().find((t) => t.code === v) as TreeRemovalReasonAppEntityDictionaryTreeRemovalReason);
      }
    });
    return result;
  }

  public get values(): string[] {
    return this.compareValues().map(v => v.id || '');
  }

  public get codeValues(): string[] {
    return this.removalReasons()
      .filter(v => [...this.values].includes(v?.id || ''))
      .map(v => v.code || '');
  }

  public override patchFormValues(ids: string[]) {
    ids.forEach(id => {
      const item = this.removalReasons().find((v) => v.id === id);
      if (!!item?.code) {
        this.get(item?.code)?.setValue(true);
      }
    });
  }
}

@Injectable()
export class TreeToRemoveItemForm extends AppForm {

  private obrebService: ObrebService = inject(ObrebService);

  public obrebList: Signal<ObrebAppEntityDictionaryObreb[] | undefined> = toSignal(this.obrebService.getObrebCollectionObrebCollection({
    pagination: false,
    orderName: 'asc'
  }));

  private treeToRemoveCommonService: TreeToRemoveCommonService = inject(TreeToRemoveCommonService);

  public treeSpecies: Signal<TreeSpeciesItem[] | undefined> = toSignal(this.treeToRemoveCommonService.loadTreeSpecies());

  public shrubSpecies: Signal<ShrubSpeciesItem[] | undefined> = toSignal(this.treeToRemoveCommonService.loadShrubSpecies());

  constructor() {
    super({
      objectType: new FormControl(null, Validators.required),
      district: new FormControl('', {nonNullable: true, validators: [Validators.maxLength(255)]}),
      registryNumber: new FormControl('', {nonNullable: true, validators: [Validators.maxLength(255)]}),
      species: new FormControl('', {nonNullable: true, validators: [Validators.required, Validators.maxLength(255)]}),
      removalFee: new FormControl({value: '0,00', disabled: true}, Validators.required),
      landDesignation: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      removalReason: new RemovalReasonForm(),
      removalJustification: new FormControl(null, [Validators.required, Validators.maxLength(5000)])
    });
  }

  public createTrunkCircumference130Control(): void {
    this.addControl('trunkCircumference130', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get trunkCircumference130(): FormControl | null {
    return this.get('trunkCircumference130') as FormControl | null;
  }

  public createTrunkCircumference130Form(): void {
    this.addControl('trunkCircumference130Form', new TrunkCircumference130Form());
  }

  public get trunkCircumference130Form(): TrunkCircumference130Form | null {
    return this.get('trunkCircumference130Form') as TrunkCircumference130Form | null;
  }

  public calcRemovalFee(treeItemFormType: ObjectTypeEnum | null): void {
    const trunkCircumference130Value: number = +(this.trunkCircumference130?.value || this.trunkCircumference130Form?.itemsSum || '0');
    const speciesValue: string | null = this.species.value;

    if (!trunkCircumference130Value || !speciesValue) {
      this.removalFee.setValue('0,00');
      return;
    }

    switch (true) {
      case treeItemFormType === 'tree': {
        const speciesItem: TreeSpeciesItem | undefined = this.treeSpecies()?.find(v => v.gatunek_drzewa === speciesValue);
        let price: number = 0;
        if (trunkCircumference130Value > 100) {
          price = +(speciesItem?.cena_za_cm_powyzej_101 || '0');
        } else {
          price = +(speciesItem?.cena_za_cm_do_100 || '0');
        }
        this.removalFee.setValue((price * trunkCircumference130Value).toFixed(2))
        break;
      }
      case treeItemFormType === 'shrub': {
        const speciesItem: ShrubSpeciesItem | undefined = this.shrubSpecies()?.find(v => v.gatunek_krzew === speciesValue);
        let price: number = 0;
        if (trunkCircumference130Value > 100) {
          price = +(speciesItem?.pow_do_100m2 || '0');
        } else {
          price = +(speciesItem?.pow_powyzej_101m2 || '0');
        }
        this.removalFee.setValue((price * trunkCircumference130Value).toFixed(2))
        break;
      }
      default: {
        this.removalFee.setValue('0,00')
      }
    }
  }

  public get species(): FormControl {
    return this.get('species') as FormControl;
  }

  public get removalFee(): FormControl {
    return this.get('removalFee') as FormControl;
  }

  public get objectType(): FormControl {
    return this.get('objectType') as FormControl;
  }

  public get removalReason(): RemovalReasonForm {
    return this.get('removalReason') as RemovalReasonForm;
  }

  public get otherRemovalReason(): FormControl | null {
    return this.get('otherRemovalReason') as FormControl | null;
  }

  public removalReasonChange(): void {
    if (this.removalReason.codeValues.includes('inna')) {
      this.addControl('otherRemovalReason', new FormControl(null, [Validators.required, Validators.maxLength(255)]))
    } else {
      this.removeControl('otherRemovalReason');
    }
  }

  public get values(): TreeToRemoveAppEntityTreeToRemoveWrite {
    return {
      species: this.value.species,
      registryNumber: this.value.registryNumber,
      district: this.value.district,
      mapSheet: this.value.mapSheet,
      landDesignation: this.value.landDesignation,
      trunkCircumference130: this.trunkCircumference130Form ? this.trunkCircumference130Form.values : this.value.trunkCircumference130,
      removalReasons: this.removalReason.values,
      objectType: this.objectType.value,
      otherRemovalReason: this.otherRemovalReason?.value || null,
      removalFee: this.removalFee.value,
      removalJustification: this.value.removalJustification
    };
  }

  public override patchFormValues(value: TreeToRemoveAppEntityTreeToRemoveWrite): void {
    super.patchFormValues({
      species: value.species,
      registryNumber: value.registryNumber,
      district: value.district,
      mapSheet: value.mapSheet,
      objectType: value?.objectType,
      removalFee: value?.removalFee,
      landDesignation: value?.landDesignation,
      removalJustification: value?.removalJustification
    });

    this.removalReason.patchFormValues(value.removalReasons || []);
    this.removalReasonChange();

    this.otherRemovalReason?.setValue(value?.otherRemovalReason);

    this.trunkCircumference130Form?.patchFormValues(value?.trunkCircumference130 || '0');
    this.trunkCircumference130?.setValue(value?.trunkCircumference130 || '0')
  }
}
